import React, { useState, useEffect, useCallback, useRef } from "react";
import { ChevronDown, ChevronUp, Trash2, Edit2, Shield } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { OwnerMultiSelect } from "@/components/findings/OwnerMultiSelect";
import { DueDatePicker } from "@/components/findings/DueDatePicker";

export interface FindingData {
  finding_title: string;
  priority_level: string;
  recommended_action: string;
  timeframe: string;
  recommended_measures: string;
  remediation_path: string;
  risk_level: string;
  risk_override: boolean;
  control_code: string;
  /** Phase 2.11.1 — multi-owner pills, persisted to assessment_findings.owners */
  owners?: string[];
  /** Phase 2.11.1 — optional due date ISO yyyy-mm-dd */
  due_date?: string | null;
}

function deriveRiskLevel(score: number | null): string {
  if (score === null || score === undefined) return "UNKNOWN";
  if (score <= 25) return "HIGH";
  if (score <= 75) return "MODERATE";
  return "LOW";
}

const RISK_COLORS: Record<string, string> = {
  HIGH: "bg-destructive text-destructive-foreground",
  MODERATE: "bg-yellow-500/90 text-white",
  LOW: "bg-green-600 text-white",
  UNKNOWN: "bg-muted text-muted-foreground",
};

interface FindingEntryProps {
  controlCode: string;
  /**
   * Phase 2.8.1 — Optional human-readable control title (e.g. "RAN Node
   * Hardening (eNodeB / gNodeB)"). When provided, it is rendered in the
   * header alongside the NIST badge. Callers must pass a human title here
   * rather than an internal control_key.
   */
  controlTitle?: string;
  reviewerScore: number | null;
  existingFinding?: Partial<FindingData> | null;
  disabled?: boolean;
  onDelete?: () => void;
  onChange?: (data: FindingData) => void;
  defaultExpanded?: boolean;
}

const FindingEntry: React.FC<FindingEntryProps> = ({
  controlCode,
  controlTitle,
  reviewerScore,
  existingFinding,
  disabled = false,
  onDelete,
  onChange,
  defaultExpanded = true,
}) => {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const [findingTitle, setFindingTitle] = useState(existingFinding?.finding_title ?? "");
  const [priorityLevel, setPriorityLevel] = useState(existingFinding?.priority_level ?? "");
  const [recommendedAction, setRecommendedAction] = useState(existingFinding?.recommended_action ?? "");
  const [timeframe, setTimeframe] = useState(existingFinding?.timeframe ?? "");
  const [recommendedMeasures, setRecommendedMeasures] = useState(existingFinding?.recommended_measures ?? "");
  const [remediationPath, setRemediationPath] = useState(existingFinding?.remediation_path ?? "");
  const [riskLevel, setRiskLevel] = useState(existingFinding?.risk_level ?? deriveRiskLevel(reviewerScore));
  const [riskOverride, setRiskOverride] = useState(existingFinding?.risk_override ?? false);
  const [showRiskDropdown, setShowRiskDropdown] = useState(existingFinding?.risk_override ?? false);
  const [owners, setOwners] = useState<string[]>(existingFinding?.owners ?? []);
  const [dueDate, setDueDate] = useState<string | null>(existingFinding?.due_date ?? null);
  const initialized = useRef(false);

  // Populate from existing finding
  useEffect(() => {
    if (existingFinding && !initialized.current) {
      setFindingTitle(existingFinding.finding_title ?? "");
      setPriorityLevel(existingFinding.priority_level ?? "");
      setRecommendedAction(existingFinding.recommended_action ?? "");
      setTimeframe(existingFinding.timeframe ?? "");
      setRecommendedMeasures(existingFinding.recommended_measures ?? "");
      setRemediationPath(existingFinding.remediation_path ?? "");
      if (existingFinding.risk_level) setRiskLevel(existingFinding.risk_level);
      if (existingFinding.risk_override) {
        setRiskOverride(true);
        setShowRiskDropdown(true);
      }
      if (Array.isArray(existingFinding.owners)) setOwners(existingFinding.owners);
      if (existingFinding.due_date !== undefined) setDueDate(existingFinding.due_date ?? null);
      initialized.current = true;
    }
  }, [existingFinding]);

  // Update derived risk when score changes (only if not overridden)
  useEffect(() => {
    if (!riskOverride) {
      setRiskLevel(deriveRiskLevel(reviewerScore));
    }
  }, [reviewerScore, riskOverride]);

  // Notify parent of changes
  const emitChange = useCallback(() => {
    onChange?.({
      finding_title: findingTitle.trim(),
      priority_level: priorityLevel,
      recommended_action: recommendedAction.trim(),
      timeframe,
      recommended_measures: recommendedMeasures.trim(),
      remediation_path: remediationPath.trim(),
      risk_level: riskLevel,
      risk_override: riskOverride,
      control_code: controlCode,
      owners,
      due_date: dueDate,
    });
  }, [findingTitle, priorityLevel, recommendedAction, timeframe, recommendedMeasures, remediationPath, riskLevel, riskOverride, controlCode, owners, dueDate, onChange]);

  // Emit on any field change
  useEffect(() => {
    if (initialized.current || findingTitle || priorityLevel || recommendedAction || owners.length || dueDate) {
      emitChange();
    }
  }, [findingTitle, priorityLevel, recommendedAction, timeframe, recommendedMeasures, remediationPath, riskLevel, riskOverride, owners, dueDate]);

  const handleRiskOverride = (value: string) => {
    setRiskLevel(value);
    setRiskOverride(true);
  };

  const handleResetRisk = () => {
    setRiskOverride(false);
    setShowRiskDropdown(false);
    setRiskLevel(deriveRiskLevel(reviewerScore));
  };

  return (
    <div className="rounded-md border border-border bg-card">
      {/* Header */}
      <button
        type="button"
        onClick={() => setExpanded(v => !v)}
        className="flex items-center justify-between w-full px-3 py-2 text-sm font-medium text-foreground hover:bg-muted/50 transition-colors"
      >
        <div className="flex items-center gap-2 min-w-0">
          {controlTitle && (
            <span className="text-sm font-semibold text-foreground truncate max-w-[260px]">
              {controlTitle}
            </span>
          )}
          {controlCode && (
            <Badge variant="outline" className="text-xs font-mono">{controlCode}</Badge>
          )}
          {findingTitle ? (
            <span className="text-xs text-muted-foreground truncate max-w-[260px]">
              — {findingTitle}
            </span>
          ) : (
            !controlTitle && (
              <span className="text-xs text-muted-foreground italic">No title</span>
            )
          )}
          <Badge className={`${RISK_COLORS[riskLevel] || RISK_COLORS.UNKNOWN} text-[10px] px-1.5 py-0`}>
            {riskLevel}{riskOverride ? " ✎" : ""}
          </Badge>
        </div>

        <div className="flex items-center gap-1">
          {onDelete && !disabled && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onDelete(); }}
              className="p-1 text-destructive/60 hover:text-destructive transition-colors"
              title="Delete finding"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          )}
          {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </div>
      </button>

      {expanded && (
        <div className="px-3 pb-3 space-y-3 border-t border-border pt-3">
          {/* Risk Level */}
          <div>
            <label className="text-xs font-medium text-muted-foreground uppercase mb-1 block">
              Risk Level
            </label>
            <div className="flex items-center gap-2">
              {showRiskDropdown ? (
                <>
                  <Select value={riskLevel} onValueChange={handleRiskOverride} disabled={disabled}>
                    <SelectTrigger className="w-[140px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="HIGH">HIGH</SelectItem>
                      <SelectItem value="MODERATE">MODERATE</SelectItem>
                      <SelectItem value="LOW">LOW</SelectItem>
                    </SelectContent>
                  </Select>
                  <button
                    type="button"
                    onClick={handleResetRisk}
                    className="text-xs text-muted-foreground hover:text-foreground underline"
                    disabled={disabled}
                  >
                    Reset to Auto
                  </button>
                </>
              ) : (
                <>
                  <Badge className={`${RISK_COLORS[riskLevel] || RISK_COLORS.UNKNOWN} text-xs px-3 py-1`}>
                    {riskLevel}
                  </Badge>
                  <button
                    type="button"
                    onClick={() => setShowRiskDropdown(true)}
                    className="text-xs text-primary hover:underline"
                    disabled={disabled}
                  >
                    Override
                  </button>
                </>
              )}
            </div>
          </div>

          {/* Finding Title */}
          <div>
            <label className="text-xs font-medium text-muted-foreground uppercase mb-1 block">Finding Title</label>
            <input
              type="text"
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
              placeholder="e.g., Partial implementation of Baseline Configuration"
              value={findingTitle}
              onChange={e => setFindingTitle(e.target.value)}
              disabled={disabled}
            />
          </div>

          {/* Priority Level */}
          <div>
            <label className="text-xs font-medium text-muted-foreground uppercase mb-1 block">Priority Level</label>
            <Select value={priorityLevel} onValueChange={setPriorityLevel} disabled={disabled}>
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select priority" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Immediate">Immediate</SelectItem>
                <SelectItem value="Quick Win">Quick Win</SelectItem>
                <SelectItem value="Strategic">Strategic</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Recommended Action */}
          <div>
            <label className="text-xs font-medium text-muted-foreground uppercase mb-1 block">Recommended Action</label>
            <textarea
              className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 resize-y"
              placeholder="e.g., Establish secure baseline configurations."
              value={recommendedAction}
              onChange={e => setRecommendedAction(e.target.value)}
              disabled={disabled}
            />
          </div>

          {/* Timeframe */}
          <div>
            <label className="text-xs font-medium text-muted-foreground uppercase mb-1 block">Timeframe</label>
            <Select value={timeframe} onValueChange={setTimeframe} disabled={disabled}>
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select timeframe" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="30–60 days">30–60 days</SelectItem>
                <SelectItem value="60–90 days">60–90 days</SelectItem>
                <SelectItem value="90–120 days">90–120 days</SelectItem>
                <SelectItem value="90–180 days">90–180 days</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Recommended Measures */}
          <div>
            <label className="text-xs font-medium text-muted-foreground uppercase mb-1 block">Recommended Measures to Lower Risk</label>
            <textarea
              className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 resize-y"
              placeholder="e.g., Develop baseline configuration policies."
              value={recommendedMeasures}
              onChange={e => setRecommendedMeasures(e.target.value)}
              disabled={disabled}
            />
          </div>

          {/* Remediation Path */}
          <div>
            <label className="text-xs font-medium text-muted-foreground uppercase mb-1 block">Real-World Remediation Path</label>
            <textarea
              className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 resize-y"
              placeholder="e.g., Engage security engineers to implement CIS baseline standards."
              value={remediationPath}
              onChange={e => setRemediationPath(e.target.value)}
              disabled={disabled}
            />
          </div>

          {/* Phase 2.11.1 — Owner(s) + Due Date (canonical, persists to assessment_findings) */}
          <div className="rounded-md border border-dashed border-border bg-muted/20 p-2 space-y-2">
            <label className="text-xs font-medium text-muted-foreground uppercase mb-1 block">Ownership</label>
            <div className="flex flex-wrap items-end gap-3">
              <div className="flex-1 min-w-[240px]">
                <OwnerMultiSelect value={owners} onChange={setOwners} disabled={disabled} />
              </div>
              <DueDatePicker value={dueDate} onChange={setDueDate} disabled={disabled} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

FindingEntry.displayName = "FindingEntry";

export function isEntryComplete(data: FindingData): boolean {
  return !!(
    data.finding_title.trim() &&
    data.priority_level &&
    data.recommended_action.trim() &&
    data.timeframe &&
    data.recommended_measures.trim() &&
    data.remediation_path.trim()
  );
}

export function hasEntryData(data: FindingData): boolean {
  return !!(data.finding_title.trim() || data.recommended_action.trim() || data.recommended_measures.trim() || data.remediation_path.trim());
}

export { FindingEntry };
